import { useDeviceType } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackChangeListClick, trackMenuItemClick } from './tracking';
import { toggleChangeListActive } from '../../actions/chatList';
import { increaseCountClosedChats } from '../../actions/chatWindow';
import { EInternetStatus } from '../../actions/internetStatus';
import { changeRoute } from '../../actions/route';
import { TabFolderMenu } from '../../components/TabFolderMenu';
import { TopMenuDesktop } from '../../components/TopMenuDesktop';
import { TopMenuMobile } from '../../components/TopMenuMobile';
import {
  chatListSelector,
  internetStatusSelector,
  isMasterSelector,
  masterFiltersSelector,
  userIdSelector,
  userSelector,
} from '../../selectors';
import { ERequestApiStatus } from '../../types/appState';
import { EDeviceType } from '../../types/deviceType';
import { EMasterTabs } from '../../types/masterFilters';
import { TRoute } from '../../types/route';
import { ETopMenuStatus } from '../../types/topMenuStatus';
import { useApplicationContext } from '../../utils/applicationContext';
import { isSubAgentsChatsEnabled } from '../../utils/isSubAgentsChatsEnabled';
import { isTopTabsRedesignedEnabled } from '../../utils/isTopTabsRedesignedEnabled';
import { ChangeListButtonContainer } from '../ChangeListButtonContainer';

type TTopMenuContainerProps = {
  isChangeListVisible: boolean;
};

export const TopMenuContainer: React.FC<TTopMenuContainerProps> = ({ isChangeListVisible }) => {
  const dispatch = useDispatch();
  const { config } = useApplicationContext();

  const { currentRoute, isChangeListActive, selectedChats, status } = useSelector(chatListSelector);
  const internetStatus = useSelector(internetStatusSelector);
  const deviceType = useDeviceType();
  const { isAuthenticated } = useSelector(userSelector);
  const { activeTab, selectedSubAgentId } = useSelector(masterFiltersSelector);
  const userId = useSelector(userIdSelector);

  const isMaster = useSelector(isMasterSelector);
  const isMasterTabsEnabled = isMaster && isSubAgentsChatsEnabled(config);

  const isTopTabsRedesigned = isTopTabsRedesignedEnabled(config);

  const onChangeRoute = React.useCallback(
    (route: TRoute) => {
      const opponentId = activeTab === EMasterTabs.SubAgentsChats && selectedSubAgentId ? selectedSubAgentId : userId;

      dispatch(changeRoute({ route }));
      trackMenuItemClick(route, opponentId);
      dispatch(increaseCountClosedChats());
    },
    [activeTab, dispatch, selectedSubAgentId, userId],
  );

  const onChangeListClick = React.useCallback(() => {
    dispatch(toggleChangeListActive());

    if (!isChangeListActive) {
      trackChangeListClick();
    }
  }, [dispatch, isChangeListActive]);

  const topMenuStatus =
    internetStatus === EInternetStatus.Offline
      ? ETopMenuStatus.Offline
      : !isAuthenticated
        ? ETopMenuStatus.NotAuthorized
        : status === ERequestApiStatus.Pending
          ? ETopMenuStatus.Loading
          : isChangeListActive
            ? ETopMenuStatus.ChangeList
            : ETopMenuStatus.Default;

  if (isTopTabsRedesigned) {
    return (
      <TabFolderMenu
        isMasterTabsEnabled={isMasterTabsEnabled}
        currentRoute={currentRoute}
        onChangeRoute={onChangeRoute}
        changeListButtonNode={<ChangeListButtonContainer />}
        status={topMenuStatus}
        isMobile={deviceType !== EDeviceType.Desktop}
      />
    );
  }

  return deviceType === EDeviceType.Desktop ? (
    <TopMenuDesktop
      currentRoute={currentRoute}
      onChangeRoute={onChangeRoute}
      onChangeListClick={onChangeListClick}
      isChangeListVisible={isChangeListVisible}
      selectedCount={selectedChats.length}
      status={topMenuStatus}
    />
  ) : (
    <TopMenuMobile
      currentRoute={currentRoute}
      onChangeRoute={onChangeRoute}
      onChangeListClick={onChangeListClick}
      isChangeListVisible={isChangeListVisible}
      selectedCount={selectedChats.length}
      status={topMenuStatus}
    />
  );
};
